import { service } from '@ember/service';
import type { ValidatorMap } from 'ember-changeset/types';
import type IntlService from 'ember-intl/services/intl';
import and from 'ember-truth-helpers/helpers/and';
import FileAttachmentDisplayer from 'parog-web/components/file-attachment-displayer';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import MedicalEventJournalEvent from 'parog-web/models/medical-event-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';

export default class MedicalEventEditionFormComponent extends CrudJournalEventBaseComponent {
  static override forcDisplayName = 'Événement médical';
  static override fullComponentId = 'medical-event/edition-form';
  static override journalEventClass = MedicalEventJournalEvent;

  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;

  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;
  override submitButtonTitle = 'Sauvegarder';

  get allInterventionTypes(): string[] {
    const interventionTypes = [
      'Hospitalisation',
      'Intervention chirurgicale',
      'Consultation spécialiste',
      'Examen médical',
      'Suivi médicamenteux',
      'État de santé',
      'Une infection',
      'Un retard de règle, une grossesse',
      'Un problème avec mon traitement',
      'Un nouveau médicament',
      'Un problème de peau',
      'Autre'
    ];

    return interventionTypes;
  }

  get comment(): string {
    return this.changeset.get('comment') || '';
  }

  get interventionType(): string {
    return this.changeset.get('interventionType') || 'type inconnu';
  }

  get occurredAt(): string {
    return this.changeset.get('occurredAt')
      ? this.intl.formatDate(this.changeset.get('occurredAt'))
      : 'date inconnue';
  }

  override get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  override afterCompleted = (): void => {
    if (!this.session.medic) this.router.transitionTo('/patient-web/chat');
    else {
      const tab = this.changeset.get('willSendMessage')
        ? 'consult-chat'
        : 'consult-timeline';
      this.router.transitionTo(`/medics-web/patients/${this.patient.id}`, {
        queryParams: { tab }
      });
    }
  };

  override async save(): Promise<typeof this.journalEventClass> {
    const model = await super.save();

    if (!this.changeset.get('willSendMessage') && !this.session.patient)
      return model;

    await this.store
      .createRecord('patient-chat-message-sent-journal-event', {
        occurredAt: new Date(),
        triggeringJournalEvent: model,
        patient: model.patient,
        creator: model.creator,
        body: 'Événement médical envoyé'
      })
      .save();

    return model;
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='field'>
          Vous pouvez déclarer ici des événements médicaux importants
          (interventions chirurgicales, consultations avec un spécialistes,
          ...). Ces informations pourront être discutées lors de vos prochains
          échanges avec l’équipe médicale.
        </div>
        <div class='field'>
          <Form.Date @labelName='Date' @propertyName='occurredAt' />
        </div>
        <div class='field'>
          <Form.Dropdown
            @labelName="Type d'événement"
            @propertyName='interventionType'
            @allowAdditions={{true}}
            as |dropdown|
          >
            {{#each this.allInterventionTypes as |value|}}
              <dropdown.Item @value={{value}} data-test-dropdown={{value}} />
            {{/each}}
          </Form.Dropdown>
        </div>
        <div class='field'>
          <Form.TextArea
            @labelName='Commentaire'
            @propertyName='comments'
            @placeholder='Apportez ici les informations utiles sur l’événement déclaré'
          />
        </div>
        <div class='field mt32 mb32'>
          <Form.FileUpload @propertyName='fileAttachmentsAttributes' />
          {{#each
            this.retrievedJournalEvent.fileAttachments
            as |fileAttachment|
          }}
            <FileAttachmentDisplayer
              @shouldDisplayDownloadLink={{true}}
              @fileAttachment={{fileAttachment}}
              @alternativeText='Événement médicale'
            />
          {{/each}}
        </div>

        {{#if (and this.session.medic @patient.authorizesAppUsage)}}
          <Form.SendMessage
            @willSendMessage={{@patient.isEnrolled}}
            @defaultChatMessage='Bonjour,
Je viens de vous adresser un nouveau document. Il est joint à ce message.
Bonne journée.'
          />
        {{/if}}
      </:content>
    </Provider>
  </template>
}
